import { createSelector } from 'reselect'
import { OrderFeedbackCollectionState } from './types'

export const selectOrderFeedbackCollectionState = (state: {
  orderFeedbackCollection: OrderFeedbackCollectionState
}) => state.orderFeedbackCollection

// `selectFormDisabled` gates the pane's CONTROLS, and "a save is in flight" is
// the only reason to disable those. Publish has a second condition of its own,
// see `selectPublishDisabled`. AI-1270 adds the RBAC gate on top of both; it
// does not replace either.
export const selectFormDisabled = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState => orderFeedbackCollectionState.isFormSubmitting
)

export const selectAllowWrittenFeedbackCollection = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState =>
    orderFeedbackCollectionState.allowWrittenFeedbackCollection
)

// The last value known to be SAVED, as opposed to the one being edited. The
// "Your current setup" card reads this one: it describes what guests are
// actually getting right now, so it must not react to an unpublished toggle.
export const selectPersistedAllowWrittenFeedbackCollection = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState =>
    orderFeedbackCollectionState.persistedAllowWrittenFeedbackCollection
)

// True when the edited value differs from the last persisted one. The CMS
// Settings precedent leaves Publish always enabled, which makes it a dead
// control on every pane load; Live Updates does track dirty state. This pane
// follows Live Updates.
export const selectIsFormDirty = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState =>
    orderFeedbackCollectionState.allowWrittenFeedbackCollection !==
    orderFeedbackCollectionState.persistedAllowWrittenFeedbackCollection
)

// Publish is disabled while a save is in flight AND whenever there is nothing
// to save.
export const selectPublishDisabled = createSelector(
  [selectFormDisabled, selectIsFormDirty],
  (formDisabled, isFormDirty) => formDisabled || !isFormDirty
)

export const selectIsFormSubmitting = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState => orderFeedbackCollectionState.isFormSubmitting
)

// The PATCH body carries this ONE key and nothing else. That is the guarantee
// behind FR-43's "turning the toggle off must not delete or hide any stored
// feedback": the merchant PATCH flips a collection flag going forward and the
// front end issues no destructive call alongside it.
export const selectApiFormattedFormData = createSelector(
  [selectOrderFeedbackCollectionState],
  orderFeedbackCollectionState => ({
    allow_written_feedback_collection:
      orderFeedbackCollectionState.allowWrittenFeedbackCollection,
  })
)
